import React, { useCallback, useMemo, useRef } from 'react'
import { Button, H6, InfoSheetPanel, Row, Separator, Divider, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { NoticeLoading, ControlSplit } from '@fs/zion-icon'
import cloneDeep from 'lodash.clonedeep'
import { getNestedOrFloatingElementById, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import PanelHeader from '../../../../../components/generic/panels/shared/PanelHeader'
import {
  getFieldText,
  getFormDefaultValues,
  getPersonsByArk,
  getPersonsFromField,
  handleSubmitLinkFieldsViaHighlights,
} from './SharedHighlightUtils'
import { getFieldsFromSharedHighlights } from '../../../../../components/generic/panels/highlightPanel/utils/highlightUtils'
import { insertHighlight } from '../../../../../utils/imageHighlightUtils'
import SharedHighlightPersonCheckbox from './SharedHighlightPersonCheckbox'
import useUnsavedChangesOverlay from '../../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesOverlay from '../../../../../components/generic/panels/shared/UnsavedChangesOverlay'

const SelectPersons = ({ fieldValue, handleSave, saving, onDiscard }) => {
  const { editSharedHighlightPage2Overlay, closeOverlay } = useOverlayContext()
  const [t] = useTranslation()
  const h = useSlsHighlightsContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const thumbnails = useSharedBrowserStateContext().thumbnails
  const isNewPerson = !getNestedOrFloatingElementById(zionSlsState.images, r2State.selectedPersonId)
  const activeField = getNestedOrFloatingElementById(zionSlsState.images, r2State.highlightsFieldInfo?.id)
  const isNewField = !activeField
  const handleClose = useCallback(() => closeOverlay('editSharedHighlightPage2'), [closeOverlay])
  const unsavedChangesOverlay = useOverlay()
  const saveButtonRef = useRef(null)

  const attachedHighlights = useMemo(
    // eslint-disable-next-line no-unsafe-optional-chaining
    () => [...r2State?.selectedHighlights.main, ...r2State?.selectedHighlights.splitScreen],
    [r2State?.selectedHighlights]
  )

  const currentFieldsOnHighlights = useMemo(() => {
    const fieldsAttachedToHighlights = getFieldsFromSharedHighlights({
      highlights: attachedHighlights,
      images: zionSlsState?.images,
      types: zionSlsState?.types,
    })

    return fieldsAttachedToHighlights || []
  }, [zionSlsState?.images, zionSlsState?.types, attachedHighlights])

  const personsFromField = useMemo(
    () =>
      getPersonsFromField({
        activeField,
        isNewPerson,
        isNewField,
        currentFieldsOnHighlights,
        images: zionSlsState.images,
        selectedPersonId: r2State.selectedPersonId,
      }),
    [activeField, isNewField, isNewPerson, r2State.selectedPersonId, currentFieldsOnHighlights, zionSlsState.images]
  )

  const allTheArks = useMemo(
    () =>
      getPersonsByArk({
        allPersonsSharingField: personsFromField,
        selectedPersonId: r2State.selectedPersonId || r2State.createdPersonId,
        currentImageArk: zionSlsState.currentImageArk,
        isNewPerson,
      }),
    [personsFromField, isNewPerson, r2State.selectedPersonId, r2State.createdPersonId, zionSlsState.currentImageArk]
  )

  const personsSharingFieldOnImage = useMemo(
    () => allTheArks[zionSlsState.currentImageArk] || [],
    [allTheArks, zionSlsState.currentImageArk]
  )

  const allPersonIdsSharingField = useMemo(() => personsFromField.map((person) => person.id), [personsFromField])

  const moreThanThirtyPersonsOnHighlights = useMemo(
    () => allPersonIdsSharingField?.length > 30,
    [allPersonIdsSharingField]
  )

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = cloneDeep(allTheArks)
      delete clone[zionSlsState.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, zionSlsState.currentImageArk])

  const headerText = useMemo(
    () =>
      moreThanThirtyPersonsOnHighlights
        ? t(
            'add-highlight-linked-fields.choose-others-on-image.more-than-thirty-heading',
            'All names selected will continue to link to the {fieldType} because the field is linked to too many names.',
            { fieldType: r2State.highlightsFieldInfo.label }
          )
        : t(
            'add-highlight-linked-fields.choose-others-on-image.heading',
            'Select every name that should continue to link to the {fieldType}.',
            { fieldType: r2State.highlightsFieldInfo.label }
          ),
    [moreThanThirtyPersonsOnHighlights, r2State.highlightsFieldInfo.label, t]
  )

  const formDefaultValues = useMemo(
    () => getFormDefaultValues({ personIds: allPersonIdsSharingField, selectedPersonId: r2State.selectedPersonId }),
    [allPersonIdsSharingField, r2State.selectedPersonId]
  )

  const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  useUnsavedChangesOverlay({ methods: formMethods, overlay: unsavedChangesOverlay, forceWarning: true })

  const linkedFieldText = useMemo(() => getFieldText(fieldValue), [fieldValue])

  /**
   * This function is called when adding highlights to a new field on a new person
   * Prepares edit highlight objects and saves them to local state to be saved when a new person is created
   * @param {Object} values ZionForm key-values representing selected persons, providing personIds
   */
  const handlePrepToLinkNewPerson = (values) => {
    // Get the list of selected person ids
    const newPersonIds = []
    Object.entries(values)
      .filter(([key]) => key.startsWith('1:1:'))
      .map(([key, value]) => {
        if (value) newPersonIds.push(key)
        return null
      })

    // Get the list of highlights being edited
    let newEditHighlights = [...r2State.editHighlights]
    attachedHighlights.forEach((selectedHighlight) => {
      selectedHighlight.personIds = newPersonIds
      selectedHighlight.selected = false
      newEditHighlights = insertHighlight(newEditHighlights, selectedHighlight)
    })
    // Prepare the objects representing edited highlights to be saved when the new person is created (see reassignHighlightDispatches() in AddPerson.js)
    r2Dispatch({ type: 'SET_EDIT_HIGHLIGHTS', value: newEditHighlights })
    const newHighlightsFieldInfo = r2State.highlightsFieldInfo
    newHighlightsFieldInfo.value = fieldValue
    r2Dispatch({ type: 'SET_HIGHLIGHTS_FIELD_INFO', value: newHighlightsFieldInfo })
    closeOverlay('highlightsPanelOld')
    closeOverlay('editSharedHighlightPage2')
    closeOverlay('editSharedHighlightPage1')
  }

  useUnsavedChangesOverlay({
    methods: formMethods,
    overlay: unsavedChangesOverlay,
    forceWarning: true,
    panelId: 'editSharedHighlightPage2',
  })

  return (
    <InfoSheetPanel
      title={t('shared-field-via-highlight.title', 'Add Linked Field')}
      headingColor="yellow"
      {...{ ...editSharedHighlightPage2Overlay, close: handleClose }}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            data-testid="panel-back-button"
            emphasis="low"
            type="submit"
            form=""
            onClick={handleClose}
            disabled={saving}
          >
            {t('edit-shared-field-footer.back', 'Back')}
          </Button>

          <Button
            data-testid="panel-forward-button"
            emphasis="high"
            disabled={saving}
            Icon={saving && NoticeLoading}
            form="add-highlight-linked-fields"
            type="submit"
            ref={saveButtonRef}
          >
            {isNewPerson ? t('edit-shared-field-footer.next', 'Next') : t('edit-shared-field-footer.save', 'Save')}
          </Button>
        </Row>
      }
    >
      <ZionForm
        onSubmit={(values) => {
          if (!isNewPerson) {
            const highlightHookDispatches = h.getDispatches({
              newFieldId: h.activeFieldInfo.id,
              mainImageArk: zionSlsState.currentImageArk,
              disassociateRectangle: false,
              splitScreenImageArk: splitScreenImageArk || undefined,
            })

            // We decided to do a hard save here, then go back to the edit panel in case the user has more changes
            // If it interrupts a shared field flow, then just go through the highlights flow and back to edit panel after saving
            // A future story will toggle the Save Changes button on the edit panel if there a no changes to save
            const dispatches = handleSubmitLinkFieldsViaHighlights({
              values,
              isNewField,
              linkedFieldValue: fieldValue,
              activeFieldInfo: r2State.highlightsFieldInfo,
              originalDispatches: [...r2State.editDispatches],
              originalHighlightDispatches: [...highlightHookDispatches],
              images: zionSlsState.images,
              sharedElements: currentFieldsOnHighlights,
              selectedHighlights: attachedHighlights,
              fieldInEdit: activeField,
              selectedPersonId: r2State.selectedPersonId,
              involvedPersons: personsFromField,
            })
            handleSave(dispatches)
          } else {
            handlePrepToLinkNewPerson(values)
          }
        }}
        methods={formMethods}
        id="add-highlight-linked-fields"
      >
        <PanelHeader
          heading={t('add-highlight-linked-fields.heading', '{fieldType}: {fieldValue}', {
            fieldType: r2State.highlightsFieldInfo.label,
            fieldValue: linkedFieldText,
          })}
          caption={t('add-highlight-linked-fields.instructions.heading', 'Fix any mistakes that you see.')}
          headingSize="H5"
        />
        <Separator />
        <H6>{headerText}</H6>
        {personsSharingFieldOnImage.map((person) => (
          <SharedHighlightPersonCheckbox
            key={person.id}
            person={person}
            isNewPerson={isNewPerson}
            fieldValue={fieldValue}
            disabled={moreThanThirtyPersonsOnHighlights}
          />
        ))}
        {allTheArksExceptCurrent &&
          Object.entries(allTheArksExceptCurrent).map(([ark, persons]) => {
            // Shouldn't this be object notation?
            const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
            const pageNumber = arkIndex + 1
            return (
              <>
                <Separator size="xs" />
                <Divider
                  label={t('add-highlight-linked-fields.names-on-page-n.label', 'Names on Page {pageNumber}', {
                    pageNumber,
                  })}
                />
                <Button
                  Icon={ControlSplit}
                  onClick={() =>
                    r2Dispatch({
                      type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
                      value: pageNumber,
                    })
                  }
                  emphasis="medium"
                >
                  {t('add-highlight-linked-fields.view-image-in-context.close.button', 'View in Context')}
                </Button>
                {persons.map((person) => (
                  <SharedHighlightPersonCheckbox
                    key={person.id}
                    person={person}
                    isNewPerson={isNewPerson}
                    fieldValue={fieldValue}
                    disabled={moreThanThirtyPersonsOnHighlights}
                  />
                ))}
              </>
            )
          })}
      </ZionForm>
      <UnsavedChangesOverlay
        overlay={unsavedChangesOverlay}
        onDiscard={onDiscard}
        onSave={() => saveButtonRef.current.click()}
        formId="review-highlight-linked-fields"
      />
    </InfoSheetPanel>
  )
}

export default SelectPersons
